<template>
 <div class="mx-sm-auto mb-sm-8 loginBox" :class="{'loginBoxSmall': !showWelcome}">
    <v-row class="mb-3" no-gutters v-if="$vuetify.breakpoint.mdAndUp && showWelcome">
      <h1 class="text-h5 font-weight-bold">{{ $t('login_page.header') }}</h1>
    </v-row>
    <v-row :class="{'mainBorder my-4': $vuetify.breakpoint.mdAndUp && border}" no-gutters>
      <v-col class="primary white--text pb-4" cols="4" v-if="$vuetify.breakpoint.mdAndUp && showWelcome">
        <div class="d-flex flex-column align-center justify-center fill-height text-center" v-if="!doSSOLookup || !hasDoneSSOLookup">
          <h3 class="text-uppercase font-weight-bold mb-1 text-h6">{{ $t('login_page.welcome') }}</h3>
          <span v-if="showSignUp" class="font-weight-medium">{{ $t('login_page.new_sign_up') }}</span>
          <v-btn v-if="showSignUp" color="white" class="primary--text my-6" :href="signupUrl" data-testid="a_1282dee2-6eee-45be-91e3-1fc237943683">{{ $t('login_page.sign_up') }}</v-btn>
        </div>
        <div v-else class="d-flex flex-column align-center justify-center fill-height">
          <h3 class="font-weight-bold mb-1 text-h6">{{ $t('login_page.welcome_back') }}</h3>
        </div>
      </v-col>
      <v-col :cols="$vuetify.breakpoint.mdAndUp && showWelcome? 8 : 12" class="pt-0 pr-0">
        <r-form-card class="bkg mb-4" :class="{'white': border}">
          <template>
            <slot name="header"></slot>
          </template>
          <div class="my-6 d-flex justify-space-between align-end">
            <h2 v-html="$t('log_in_to_your_account')" class="text-h6"></h2>
            <language-button v-if="hasFeature('languageTranslations')" class="ml-4"/>
          </div>
          <div v-if="!showPasswordLogin">
            <v-alert :value="error.length > 0" dense text type="error" data-testid="v-alert_c583b978-94c4-481f-9dc3-b8aa0f7ac2dd">
              {{error}}
            </v-alert>
          </div>
          <slot name="formprefix">
          </slot>
          <div v-if="showPasswordLogin">
            <v-form ref="loginform" v-model="valid" lazy-validation>
              <div v-if="codeInput == false" v-on:click="emailClick">
                <v-text-field outlined dense v-model="email" name="username" autocomplete="username" :rules="requiredRules" :readonly="doingSSOLookup || hasDoneSSOLookup"
                ref="email" @keydown.enter="!doSSOLookup || hasDoneSSOLookup ? login() : performSSOLookup()" v-bind:label="$t('email')" @input="validateEmail()"
                :error="emailError.length > 0" :error-messages="emailError" data-testid="email-input"/>
              </div>
              <div v-else>
                <v-text-field outlined dense v-model="code" :rules="requiredRules" ref="code" @keydown.enter="performCodeAuth();"
                v-bind:label="$t('authentication_code')" data-testid="code-input"/>
              </div>
              <v-text-field outlined dense v-show="!doSSOLookup || hasDoneSSOLookup" name="password" autocomplete="current-password" v-model="password" :rules="passwordRules"
              v-bind:label="$t('password')" type="password" @keydown.enter="login" data-testid="password-input"/>
              <div v-if="codeInputEnabled && !hasDoneSSOLookup" class="text-right">
                <a v-if="codeInput == false" @click.prevent="codeInput = true">{{ $t('have_auth_code') }}</a>
                <a v-else @click.prevent="codeInput = false">{{ $t('use_email') }}</a>
              </div>
              <v-alert :value="error.length > 0" dense text type="error" data-testid="v-alert_c583b978-94c4-481f-9dc3-b8aa0f7ac2dd">
                {{error}}
              </v-alert>
              <v-checkbox v-if="showRememberMe" v-model="rememberMe" :label="$t('remember_me')"></v-checkbox>
              <router-link :to="passwordResetRoute" color="secondary" class="keepnewlines">{{ $t('forgot_password') }}</router-link>
              <div style="min-height: 20vh">
                <div class="buttondiv mt-6">
                  <r-form-submit-button v-if="codeInput == true" :disabled="!valid" v-on:click="performCodeAuth()" data-testid="code-continue-button">{{ $t("continue") }}</r-form-submit-button>
                  <r-form-submit-button v-else-if="!doSSOLookup || hasDoneSSOLookup" :loading="doingLogin" :disabled="!valid" v-on:click="login" data-testid="login-button">{{ $t("login") }}</r-form-submit-button>
                  <r-form-submit-button v-else :loading="doingSSOLookup" :disabled="!valid || doingSSOLookup" v-on:click="performSSOLookup()" data-testid="continue-button">{{ $t("continue") }}</r-form-submit-button>
                </div>
                <r-or-border v-if="office365LoginUrl" class="text-uppercase mt-sm-2">{{ $t("or") }}</r-or-border>
                <div class="d-flex flex-wrap justify-center mb-4">
                  <v-btn class="mt-2" outlined color="grey darken-2"  v-if="office365LoginUrl" v-on:click="office365login" data-testid="microsoft-button">
                    <v-img class="px-2" src="static/images/ms-logo-64.png" contain max-width="32px" />
                      {{ $t("log_in_with_microsoft") }}
                    </v-btn>
                  <slot name="other-options"/>
                </div>
              </div>
            </v-form>
          </div>
          <slot name="formpostfix-inner">
          </slot>
          <div class="mb-4">
            <a target="_blank" v-if="showTermsAndConditions && getTermsAndConditionsUrl() && !hasDoneSSOLookup" class="terms keepnewlines text-right" :href="getTermsAndConditionsUrl()" data-testid="a_terms_b558fbde-d642-413f-98a1-d26f2dd15b0f">{{ $t('terms_and_conditions') }}</a>
            <div class="mt-4" v-if="$vuetify.breakpoint.smAndDown && showSignUp && showWelcome && !hasDoneSSOLookup">
              {{ $t('dont_have_account_yet') }} <a :href="signupUrl" data-testid="a_b14a5ad3-57b0-453d-b804-a6276208a2e0">{{ $t('login_page.sign_up') }}</a>
            </div>
          </div>
          <slot name="footer"></slot>
          <slot name="append"></slot>
          <!--<floating-translation-select-menu v-if="$root.getSetting('show_login_translation_select')" />-->
          <login-option-list-dialog :loginOptions="loginOptions" @useOption="useLoginOption($event)"/>
          <v-dialog v-model="showMFA" :width="400">
            <mfa-entry :key="showMFA" @login="loginWithMFA($event)"/>
          </v-dialog>
        </r-form-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { dashboardUserService } from 'reactor-js-core/dist/services/dashboarduserservice.js'
import { samlService } from 'reactor-js-core/dist/services/samlservice.js'
import { tenancyService } from 'reactor-js-core/dist/services/tenancyservice.js'
import { persistentStorage } from 'reactor-js-core/dist/persistentstorage.js'
//import FloatingTranslationSelectMenu from '@/components/ui/FloatingTranslationSelectMenu.vue'
import LoginOptionListDialog from './LoginOptionListDialog.vue'
import LanguageButton from '@/components/ui/LanguageButton'
import { sharedReactorWebsocket as ws} from 'reactor-js-core';
import MFAEntry from './MFAEntry'
import { mapGetters } from 'vuex'
import emailValidation from '@/emailvalidation'

export default {
  name: 'LoginFormPartial',
  components: {
    //FloatingTranslationSelectMenu,
    LoginOptionListDialog,
    LanguageButton,
    MfaEntry: MFAEntry
  },
  props: {
    office365LoginUrl: {
      type: String,
      default: null
    },
    showPasswordLogin: {
      type: Boolean,
      default: true
    },
    doSSOLookup: {
      type: Boolean,
      default: false
    },
    loginType: {
      type: String,
      default: null
    },
    redirectVersion: {
      type: String,
      default: null
    },
    samlForceAuth: {
      type: String,
      default: null
    },
    codeInputEnabled: {
      type: Boolean,
      default: false
    },
    showRememberMe: {
      type: Boolean,
      default: false
    },
    showTermsAndConditions: {
      type: Boolean,
      default: true
    },
    showWelcome: {
      type: Boolean,
      default: false
    },
    border: {
      type: Boolean,
      default: true
    },
    signupUrl: {
      type: String,
      default: "https://signup.vevox.com/#/"
    },
  },
  data () {
    return {
      valid: false,
      email: '',
      emailError: '',
      requiredRules: [
        value => {
          if (value == null || value.trim().length == 0) return this.$t('required');
          return true;
        }
      ],
      password: '',
      error: '',
      lockedOutSubscription: null,
      hasDoneSSOLookup: false,
      doingSSOLookup: false,
      rememberMe: false,
      codeInput: false,
      code: '',
      loginOptions: null,
      showMFA: false,
      doingLogin: false,
      tenancyUrlInfoPromise: null,
    }
  },
  computed: {
    passwordRules() {
      if (!this.doSSOLookup || this.hasDoneSSOLookup) {
        return this.requiredRules;
      }
      return [];
    },
    ...mapGetters({
      hasFeature: 'featureset/hasFeature'
    }),
    passwordResetRoute() {
      let route = {name: 'ResetPassword'};
      if (this.loginType != null) {
        route.query = { logintype: this.loginType };
      }
      return route;
    },
    showSignUp() {
      return this.loginType != 'pptlogin' && this.loginType != 'jspptlogin' && this.loginType != 'teamslogin';
    }
  },
  methods: {
    getTenancyUrlInfo() {
      if (this.tenancyUrlInfoPromise == null) {
        this.tenancyUrlInfoPromise = tenancyService.getTenancyUrlInfo({url: this.$root.getPlayDashboardHostName(), includeInactive: true}).then((response) => {
          return response;
        });
      }
      return this.tenancyUrlInfoPromise;
    },
    getTermsAndConditionsUrl() {
      var url = this.$t('terms_and_conditions_url');
      if ("terms_and_conditions_url" === url) {
        return null;
      }
      return url;
    },
    setError(error) {
      this.error = error;
    },
    office365login: function() {
      this.getTenancyUrlInfo().then(response => {
        if (response.tenancyId != null && response.hasOwnProperty('tenancyActive') && !response.tenancyActive && response.inactiveMessage) {
          var message = {
            title: this.$t('tenancy_inactive'),
            trusted_html: response.inactiveMessage
          };
          this.$root.alert(message, () => {
            this.tenancyUrlInfoPromise = null; //reset the promise so we can try again
          });
          return;
        }
        this.setError('');
        this.$emit('office365login', this.office365LoginUrl);
      });
    },
    redirect: function(url) {
      window.location.href = url;
    },
    login: function (event) {
      this.getTenancyUrlInfo().then(response => {
        if (response.tenancyId != null && response.hasOwnProperty('tenancyActive') && !response.tenancyActive && response.inactiveMessage) {
          var message = {
            title: this.$t('tenancy_inactive'),
            trusted_html: response.inactiveMessage
          };
          this.$root.alert(message, () => {
            this.tenancyUrlInfoPromise = null; //reset the promise so we can try again
          });
          return;
        }
        this.setError('');
        if (this.$refs.loginform.validate()) {
          this.$emit('login', this.email, this.password);
        }
        this.doingLogin = true;
      });
    },
    loginWithMFA(mfa_code) {
      this.getTenancyUrlInfo().then(response => {
        if (response.tenancyId != null && response.hasOwnProperty('tenancyActive') && !response.tenancyActive && response.inactiveMessage) {
          var message = {
            title: this.$t('tenancy_inactive'),
            trusted_html: response.inactiveMessage
          };
          this.$root.alert(message, () => {
            this.tenancyUrlInfoPromise = null; //reset the promise so we can try again
          });
          return;
        }
        this.showMFA = false;
        this.setError('');
        this.$emit('loginWithMFA', {
          email: this.email,
          password: this.password,
          mfaCode: mfa_code
        });
      })
    },
    resetPassword: function(reason) {
      this.setError('');
      this.$router.push({ name: 'ResetPassword', params: { returnPath: this.$router.currentRoute.path, reason: reason } })
    },
    lockedOutUrl: function() {
      var base = window.location.protocol + "//" + window.location.host + "/";
      var locked_out_url =  base + "#/confirm?login_id=[LOGIN_ID]&user_id=[USER_ID]&login_token=[LOGIN_TOKEN]";
      return locked_out_url;
    },
    loginResponse: function(response) {
      this.doingLogin = false;
      if (response.status === 'success') {
        this.loginSuccess(response);
      } else {
        this.loginFailure(response);
      }
    },
    loginSuccess(response) {
      this.$emit('loginSuccess', response);
    },
    loginFailure(response) {
      this.tenancyUrlInfoPromise = null; //reset the promise so we can try again
      if (response.exceptionName == 'AuthenticationException') {
        if (this.hasFeature('multiFactorAuthentication')) {
          this.setError(this.$t('login_credentials_not_recognised'));
        }
        else {
          this.setError(this.$t('username_password_not_recognised'));
        }
      } else if (response.exceptionName === "RequiresAdditionalAuthorizationException") {
        this.handleLockedOut(response);
      } else if (response.exceptionName === "PendingAuthVerificationException") {
        this.setError(this.$t('login_pending_auth_verification', { email: this.email }));
      } else if (response.exceptionName === "NoRolesForTenancyException") {
        this.setError(this.$t('login_no_access'));
      } else if (response.exceptionName === "UserInactiveException") {
        this.setError(this.$t('login_user_inactive'));
      } else if (response.exceptionName === "UserPasswordTooWeakException") {
        this.resetPassword("UserPasswordTooWeakException");
      } else if (response.exceptionName === "MFARequiredException") {
        this.showMFA = true;
      } else {
        this.setError(this.$t('undefined_error'));
      }
    },
    handleLockedOut(response) {
      this.setError(this.$t('user_locked_out'));
      persistentStorage.set("pendingLoginToken", response.exceptionData.loginId);

      if (this.lockedOutSubscription != null) {
        this.lockedOutSubscription.unsubscribe();
        this.lockedOutSubscription = null;
      }

      this.lockedOutSubscription = dashboardUserService.subscribeToCompletedLogin(response.exceptionData.loginId, success => {
        if (success.status !== 'success') {
          this.setError(this.$t('undefined_error'));
        }
      });
      this.lockedOutSubscription.addHandler('DashboardUserLoginCompletedBroadcast', msg => {
        this.lockedOutSubscription.unsubscribe();
        this.lockedOutSubscription = null;
        if (msg.loginId != null) {
          this.setError('');
          this.loginWithLoginIdWhenLockedOut(msg.loginId);
        } else {
          this.setError(this.$t('undefined_error'));
        }
      });
    },
    loginWithLoginIdWhenLockedOut(login_id) {
      this.$emit('loginWithLoginIdWhenLockedOut', login_id);
    },
    performSSOLookup() {
      this.setError('');
      if (this.$refs.loginform.validate()) {
        this.doingSSOLookup = true;
        dashboardUserService.lookupLoginEmail({email: this.email}).then(response => {
          this.doingSSOLookup = false;
          if (response.loginOptions.length > 0) {
            if (response.loginOptions.length == 1) {
              this.useLoginOption(response.loginOptions[0]);
            }
            else {
              this.loginOptions = response.loginOptions;
            }
          }
          else {
            //no options returned -> show password
            this.hasDoneSSOLookup = true;
            this.$emit("hasDoneSSOLookup", true);
            this.loginOptions = null;
          }
        }, (failure) => {
          console.log("Could not do getSAMLOptionsByEmail " + failure);
          this.doingSSOLookup = false;
          this.hasDoneSSOLookup = true;
          this.$emit("hasDoneSSOLookup", true);
        });
      }
    },
    doSSOLogin(sso_login_url) {
      if (this.loginType == null) {
        window.location = sso_login_url;
      }
      else {
        let state = {
          type: this.loginType,
          rememberMe: this.rememberMe,
          version: this.redirectVersion,
        }
        let force_auth_param = this.samlForceAuth != null ? '&saml_force_auth=' + this.samlForceAuth : '';
        window.location = sso_login_url + "?state=" + encodeURIComponent(JSON.stringify(state)) + force_auth_param;
      }
    },
    useLoginOption(login_option) {
      if (login_option['@type'] === "Password") {
        this.hasDoneSSOLookup = true;
        this.$emit("hasDoneSSOLookup", false);

        //reconnect to the other cluster
        ws.disconnect();

        let ws_url = login_option.regionURL.replace("http://", "ws://").replace("https://", "wss://") + "/api";
        ws.connect(ws_url);
        this.loginOptions = null;
      } else if (login_option['@type'] === "SSO") {
        this.doSSOLogin(login_option.ssoLoginURL);
      }
    },
    emailClick() {
      if (this.hasDoneSSOLookup) {
        this.password = "";
        this.hasDoneSSOLookup = false;
        this.$emit("hasDoneSSOLookup", false);
      }
    },
    performCodeAuth() {
      this.$emit("codeauth", this.code);
    },
    validateEmail() {
      var r = emailValidation.validate(this.email.trim(), this.$t);
      if (r == true) {
        this.valid = true;
        this.emailError = '';
        return true;
      }
      this.valid = false;
      this.emailError = r;
      return false;
    },
    applyCustomBranding() {
      const this_ = this;
      this_.$root.setCustomLogo("");
      this_.getTenancyUrlInfo().then((response) => {
        if (response.tenancyId != null && response.hasOwnProperty('tenancyActive') && !response.tenancyActive && response.inactiveMessage) {
           var message = {
             title: this_.$t('tenancy_inactive'),
             trusted_html: response.inactiveMessage
           };
           this_.$root.alert(message, () => {
           });
        }
        if (response.customHighlightColor) {
          this_.$root.setCustomPrimaryColor(response.customHighlightColor);
        } else {
          this_.$root.resetCustomPrimaryColor();
        }
        if (response.customLogoUrl) {
          this_.$root.setCustomLogo(response.customLogoUrl);
        } else {
          this_.$root.resetCustomLogo();
        }
      })
      .catch(failure => {
        this.$root.resetCustomPrimaryColor();
        this.$root.resetCustomLogo();
      });
    },
  },
  watch: {
    rememberMe(remember_me) {
      persistentStorage.set('rememberMe', remember_me);
    },
    showRememberMe(show_remember_me) {
      if (show_remember_me) {
        persistentStorage.set('rememberMe', this.rememberMe);
      }
    }
  },
  mounted() {
    if (this.showRememberMe) {
      persistentStorage.set('rememberMe', this.rememberMe);
    }
    this.applyCustomBranding();
  },
  beforeDestroy() {
    if (this.lockedOutSubscription != null) {
      this.lockedOutSubscription.unsubscribe();
      this.lockedOutSubscription = null;
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.buttondiv {
  text-align: center;
}
/*.resetpassword {
  padding-top: 10px;
}
.terms {
  float: right;
}
.text-h5 {
  font-size: 24px!important;
}
.keepnewlines {
  white-space: pre-wrap;
}*/

.loginBox {
  width: 100%;
}

.loginBoxSmall {
  max-width: 600px;
}

.mainBorder {
  border: 1px solid #e0e0e0;
}

.logo {
  width: 160px;
}

.bkg {
  color: inherit;
}
</style>



// WEBPACK FOOTER //
// src/components/login/LoginFormPartial.vue